import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Divider, MenuItem, Pagination, Select, IconButton, Tooltip } from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  SwapHoriz,
  AccountBalanceWallet,
  Gavel,
  Build,
  TrendingUp,
  TrendingDown,
  History,
  OpenInNew,
  Sort,
  ShoppingBag
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/axios';
import { RootState } from '../../../../store';
import { formatMoney, moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { RARITY_COLORS, RarityGrade } from '../../../utils/RarityColors';

// --- Types ---

export type LedgerReferenceType = 'TRADE' | 'PAYMENT_TRANSACTION' | 'USER_RESTRICTION' | 'ADMIN_ADJUSTMENT';

export interface SystemLedgerEntryResponseDTO {
  id: number | string;
  entryType: string;
  amountMinor: number;
  senderUserId: number | string | null;
  receiverUserId: number | string | null;
  onHold: boolean;
}

export interface ProfileLedgerTransactionResponseDTO {
  id: number | string;
  status: string;
  referenceType: LedgerReferenceType;
  referenceId: number | string | null;
  totalGrossAmountMinor: number;
  creationDate: string; // ISO LocalDateTime
  entries: SystemLedgerEntryResponseDTO[];
}

export interface TradeDTO {
  id: number | string;
  offerId: number | string;
  buyGroupId: number | string;
  sellGroupId: number | string;
  tradeGroupId?: number | string | null;
  tradeGroupLocked?: boolean | null;
  priceMinor: number;
  item?: {
    id?: number | string;
    name?: string;
    iconUrl?: string;
    imageLink?: string;
    marketHashName?: string;
    rarity?: string;
    catalog?: {
      name?: string;
      marketHashName?: string;
      imageLink?: string;
      iconUrl?: string;
    };
    details?: {
      rarity?: string;
      floatValue?: number;
    };
  };
  sellerUserId: number | string;
  sellerSteamAccountId: number | string;
  sellerSteamAvatarUrl?: string;
  sellerSteamProfileUrl?: string;
  buyerUserId: number | string;
  buyerSteamAccountId: number | string;
  buyerSteamAvatarUrl?: string;
  buyerSteamProfileUrl?: string;
  snapshotJson?: string;
  creationDate: string;
}

export interface ProfileLedgerGroupResponseDTO {
  id: number | string;
  status: string;
  referenceType: LedgerReferenceType | null;
  referenceId: number | string | null;
  totalGrossAmountMinor: number;
  creationDate: string | null;
  trades: TradeDTO[];
  transactions: ProfileLedgerTransactionResponseDTO[];
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}

const UserTransactions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('id') || '';

  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  // Resolves current user ID from state safely
  const authState = useSelector((state: RootState) => state.auth);
  const currentUserId = authState?.userId ?? (authState as any)?.user?.id ?? (authState as any)?.id;

  const [groups, setGroups] = useState<ProfileLedgerGroupResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [filter, setFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [sortBy, setSortBy] = useState<string>('creationDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
      setPage(1);
    }
  }, [urlSearch]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page - 1).toString(),
        size: pageSize.toString(),
        search: searchQuery,
        sort: `${sortBy},${sortDir}`
      });
      if (filter !== 'ALL') params.append('referenceType', filter);

      const res = await api.get<PageResponse<ProfileLedgerGroupResponseDTO>>(`/profile/transactions?${params.toString()}`);
      setGroups(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);

      if (res.data.content.length === 1 && searchQuery) {
        setExpandedId(res.data.content[0].id);
      }
    } catch (error) {
      console.error('Failed to load transactions', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, searchQuery, sortBy, sortDir]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getReferenceDetails = (type: LedgerReferenceType | null) => {
    switch (type) {
      case 'TRADE':
        return { label: t('transactions.types.trade'), icon: <SwapHoriz className="text-blue-500" />, color: 'border-blue-100 dark:border-blue-500/20' };
      case 'PAYMENT_TRANSACTION':
        return { label: t('transactions.types.payment'), icon: <AccountBalanceWallet className="text-green-500" />, color: 'border-green-100 dark:border-green-500/20' };
      case 'USER_RESTRICTION':
        return { label: t('transactions.types.restriction'), icon: <Gavel className="text-red-500" />, color: 'border-red-100 dark:border-red-500/20' };
      case 'ADMIN_ADJUSTMENT':
        return { label: t('transactions.types.adjustment'), icon: <Build className="text-gray-500" />, color: 'border-gray-100 dark:border-gray-500/20' };
      default:
        return { label: type || t('transactions.types.general'), icon: <SwapHoriz />, color: 'border-gray-100 dark:border-white/5' };
    }
  };

  const formatEntryType = (type: string) => {
    return t(`transactions.entries.${type.toLowerCase()}`, { defaultValue: type });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper method for loose string/number comparison
  const matchesUserId = useCallback((id1: any, id2: any) => {
    if (id1 == null || id2 == null) return false;
    return String(id1) === String(id2);
  }, []);

  const renderPrice = (minorAmount: number, isPositive: boolean) => {
    const displayValue = formatMoney(moneyFromMinor(Math.abs(minorAmount)) * currencyRate);
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';

    return (
        <span className={`font-mono font-bold ${colorClass}`}>
        {isPositive ? '+' : '-'}{currencySymbol}{displayValue}
      </span>
    );
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(1);
    setSearchParams(val ? { id: val } : {}, { replace: true });
  };

  // Resolves matching transactions for a specific trade instance
  const getTransactionsForTrade = (trade: TradeDTO, index: number, allTrades: TradeDTO[], allTransactions: ProfileLedgerTransactionResponseDTO[]) => {
    if (!allTransactions || allTransactions.length === 0) return [];

    const matched = allTransactions.filter((tx) => {
      const refId = String(tx.referenceId);
      return (
          refId === String(trade.id) ||
          refId === String(trade.offerId) ||
          (trade.tradeGroupId && refId === String(trade.tradeGroupId)) ||
          (trade.buyGroupId && refId === String(trade.buyGroupId)) ||
          (trade.sellGroupId && refId === String(trade.sellGroupId))
      );
    });

    if (matched.length > 0) return matched;
    if (allTrades.length === 1) return allTransactions;
    if (allTrades.length === allTransactions.length && allTransactions[index]) return [allTransactions[index]];
    return allTransactions;
  };

  // Extracts and calculates entry rows for a specific trade
  const getEntriesForTrade = (trade: TradeDTO, tradeTxs: ProfileLedgerTransactionResponseDTO[]) => {
    const isSeller = matchesUserId(trade.sellerUserId, currentUserId);
    const isBuyer = matchesUserId(trade.buyerUserId, currentUserId);

    const allEntries: SystemLedgerEntryResponseDTO[] = [];
    tradeTxs.forEach((tx) => {
      if (tx.entries) allEntries.push(...tx.entries);
    });

    const relevant: { entry: SystemLedgerEntryResponseDTO; isPositive: boolean; amountMinor: number }[] = [];

    allEntries.forEach((entry) => {
      const userIsReceiver = matchesUserId(entry.receiverUserId, currentUserId);
      const userIsSender = matchesUserId(entry.senderUserId, currentUserId);

      if (userIsReceiver) {
        relevant.push({ entry, isPositive: true, amountMinor: entry.amountMinor });
      } else if (userIsSender) {
        relevant.push({ entry, isPositive: false, amountMinor: entry.amountMinor });
      } else if (isSeller) {
        const isFee = entry.entryType.toUpperCase().includes('FEE');
        if (isFee) {
          relevant.push({ entry, isPositive: false, amountMinor: entry.amountMinor });
        } else {
          relevant.push({ entry, isPositive: true, amountMinor: entry.amountMinor });
        }
      } else if (isBuyer) {
        const isFee = entry.entryType.toUpperCase().includes('FEE');
        if (!isFee) {
          relevant.push({ entry, isPositive: false, amountMinor: entry.amountMinor });
        }
      } else {
        const isPos = entry.receiverUserId != null ? matchesUserId(entry.receiverUserId, currentUserId) : entry.amountMinor >= 0;
        relevant.push({ entry, isPositive: isPos, amountMinor: entry.amountMinor });
      }
    });

    let netMinor = 0;
    relevant.forEach((item) => {
      if (item.isPositive) netMinor += item.amountMinor;
      else netMinor -= item.amountMinor;
    });

    if (relevant.length === 0) {
      netMinor = isSeller ? trade.priceMinor : -trade.priceMinor;
    }

    return { relevant, netMinor };
  };

  return (
      <div className="flex flex-col py-4 px-4 bg-white dark:bg-neutral-900/40 shadow-sm dark:shadow-lg border border-gray-100 dark:border-white/5 min-h-[88vh] max-h-[88vh]">
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter shrink-0">
            {t('transactions.title')}
          </h2>

          <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto justify-end">
            <input
                type="text"
                placeholder={t('transactions.search_placeholder')}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-white/5 rounded-lg py-2 px-3 text-[11px] text-gray-700 dark:text-gray-200 focus:outline-none focus:border-blue-500 w-full sm:w-40 transition-all"
            />

            <Select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                size="small"
                className="bg-gray-50 dark:bg-neutral-800/50 text-gray-700 dark:text-gray-300 !text-[11px] rounded-lg min-w-[120px]"
                sx={{ height: '36px', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)' } }}
            >
              <MenuItem value="ALL" className="!text-xs">{t('transactions.filters.all')}</MenuItem>
              <MenuItem value="TRADE" className="!text-xs">{t('transactions.filters.trade')}</MenuItem>
              <MenuItem value="PAYMENT_TRANSACTION" className="!text-xs">{t('transactions.filters.payment')}</MenuItem>
              <MenuItem value="USER_RESTRICTION" className="!text-xs">{t('transactions.filters.restriction')}</MenuItem>
              <MenuItem value="ADMIN_ADJUSTMENT" className="!text-xs">{t('transactions.filters.adjustment')}</MenuItem>
            </Select>

            <Select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                size="small"
                className="bg-gray-50 dark:bg-neutral-800/50 text-gray-700 dark:text-gray-300 !text-[11px] rounded-lg min-w-[110px]"
                sx={{ height: '36px', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)' } }}
                startAdornment={<Sort sx={{ fontSize: 16, mr: 1, opacity: 0.5 }} />}
            >
              <MenuItem value="creationDate" className="!text-xs">{t('transactions.sort.date')}</MenuItem>
              <MenuItem value="id" className="!text-xs">{t('transactions.sort.id')}</MenuItem>
              <MenuItem value="totalGrossAmountMinor" className="!text-xs">{t('transactions.sort.amount')}</MenuItem>
            </Select>

            <IconButton
                size="small"
                onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-white/5 !rounded-lg h-[36px] w-[36px]"
            >
              {sortDir === 'desc' ? <TrendingDown fontSize="small" /> : <TrendingUp fontSize="small" />}
            </IconButton>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5" />

        {/* Updated: flex flex-col to allow children to use full height for centering */}
        <div className="flex-1 flex flex-col overflow-y-auto mt-4 pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-800">
          {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <CircularProgress size={35} className="text-blue-500" />
              </div>
          ) : groups.length === 0 ? (
              /* flex-1 makes this div fill the entire available space in the scroll container */
              <div className="flex-1 flex flex-col items-center justify-center opacity-40">
                <History sx={{ fontSize: 60 }} className="text-gray-300 mb-2" />
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t('transactions.empty')}</p>
              </div>
          ) : (
              <div className="space-y-2">
                {groups.map((group) => {
                  const details = getReferenceDetails(group.referenceType);
                  const isPositive = group.totalGrossAmountMinor >= 0;
                  const isExpanded = expandedId === group.id;

                  return (
                      <div key={group.id} className={`border ${details.color} rounded-xl bg-gray-50/30 dark:bg-neutral-900/20 overflow-hidden transition-all duration-200`}>
                        <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100/50 dark:hover:bg-white/[0.03]"
                            onClick={() => setExpandedId(isExpanded ? null : group.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-gray-100 dark:bg-white/5 rounded-lg shrink-0">{details.icon}</div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{details.label}</span>
                                <span className="text-[10px] text-gray-400 font-mono italic">#{group.id}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-500 font-medium">
                            {formatDateTime(group.creationDate)}
                          </span>
                                {group.referenceType === 'TRADE' && group.referenceId && (
                                    <Tooltip title={t('transactions.tooltips.view_trade')} arrow>
                                      <IconButton
                                          size="small"
                                          className="!p-0.5"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/profile/trades/${isPositive ? 'sold' : 'purchased'}?id=${group.referenceId}`);
                                          }}
                                      >
                                        <OpenInNew sx={{ fontSize: 13 }} className="text-blue-500 hover:text-blue-400" />
                                      </IconButton>
                                    </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 shrink-0">
                            <div className="text-right">
                              <div className="text-sm">
                                {renderPrice(group.totalGrossAmountMinor, isPositive)}
                              </div>
                              <div className="text-[9px] text-gray-400 font-bold uppercase mt-1">
                                {isPositive ? t('transactions.status.credit') : t('transactions.status.debit')}
                              </div>
                            </div>
                            {isExpanded ? <KeyboardArrowUp className="text-gray-400" /> : <KeyboardArrowDown className="text-gray-400" />}
                          </div>
                        </div>

                        {isExpanded && (
                            <div className="px-4 pb-4 space-y-3">
                              {/* Trade details section if trades are present in the group */}
                              {group.trades && group.trades.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1">
                                      <ShoppingBag sx={{ fontSize: 14 }} />
                                      <span>{t('transactions.details.trades')} ({group.trades.length})</span>
                                    </div>

                                    {group.trades.map((trade, idx) => {
                                      const img = trade.item?.catalog?.imageLink || trade.item?.catalog?.iconUrl || trade.item?.iconUrl || trade.item?.imageLink || '';
                                      const rarity = trade.item?.details?.rarity || trade.item?.rarity || '';
                                      const rarityColor = RARITY_COLORS[rarity as RarityGrade] ?? '#ffffff';
                                      const itemName = trade.item?.catalog?.marketHashName || trade.item?.catalog?.name || trade.item?.marketHashName || trade.item?.name || `#${trade.id}`;

                                      const tradeTxs = getTransactionsForTrade(trade, idx, group.trades, group.transactions);
                                      const { relevant, netMinor } = getEntriesForTrade(trade, tradeTxs);
                                      const isNetPositive = netMinor >= 0;

                                      return (
                                          <div key={trade.id} className="bg-white dark:bg-black/30 rounded-lg p-3 space-y-2 border border-gray-100 dark:border-white/[0.03]">
                                            <div className="flex items-center justify-between text-xs">
                                              <div className="flex items-center gap-3 min-w-0">
                                                <div className="relative flex items-center shrink-0">
                                                  <div style={{ width: 3, height: 38, background: rarityColor }} />

                                                  <div className="relative ml-1">
                                                    {img ? (
                                                        <img
                                                            src={img}
                                                            alt={itemName}
                                                            className="w-10 h-10 object-contain z-10 relative drop-shadow-md"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 bg-neutral-700 z-10 relative rounded-sm" />
                                                    )}
                                                    <div
                                                        style={{
                                                          position: 'absolute',
                                                          top: '50%',
                                                          left: -4,
                                                          transform: 'translateY(-50%)',
                                                          width: 44,
                                                          height: 38,
                                                          background: `linear-gradient(to right, ${rarityColor}22, transparent)`,
                                                          zIndex: 0
                                                        }}
                                                    />
                                                  </div>
                                                </div>

                                                <span className="truncate text-gray-700 dark:text-gray-200 font-bold text-xs">
                                      {itemName}
                                    </span>
                                              </div>

                                              <div className="text-right shrink-0 ml-2">
                                                {renderPrice(netMinor, isNetPositive)}
                                              </div>
                                            </div>

                                            {relevant.length > 0 && (
                                                <div className="pt-2 border-t border-gray-100 dark:border-white/5 space-y-1 pl-2">
                                                  <div className="flex justify-between items-center px-1 pb-1 text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                                                    <span>{t('transactions.details.description')}</span>
                                                    <span>{t('transactions.details.amount')}</span>
                                                  </div>
                                                  {relevant.map(({ entry, isPositive: entryIsPositive }) => (
                                                      <div key={entry.id} className="flex items-center justify-between text-xs py-0.5 px-1 hover:bg-gray-50 dark:hover:bg-white/[0.02] rounded">
                                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                                          {entryIsPositive ? <TrendingUp className="text-green-500" sx={{ fontSize: 14 }} /> : <TrendingDown className="text-red-500" sx={{ fontSize: 14 }} />}
                                                          <span className="text-[11px] font-medium">{formatEntryType(entry.entryType)}</span>
                                                          {entry.onHold && (
                                                              <span className="text-[9px] bg-yellow-100 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-600 px-1 rounded font-bold uppercase">
                                              {t('transactions.status.on_hold')}
                                            </span>
                                                          )}
                                                        </div>
                                                        <div className="text-right">
                                                          {renderPrice(entry.amountMinor, entryIsPositive)}
                                                        </div>
                                                      </div>
                                                  ))}
                                                </div>
                                            )}
                                          </div>
                                      );
                                    })}
                                  </div>
                              )}

                              {/* Standalone non-trade transactions section */}
                              {(!group.trades || group.trades.length === 0) && group.transactions && group.transactions.length > 0 && group.transactions.map((tx) => (
                                  <div key={tx.id} className="bg-white dark:bg-black/30 rounded-lg p-3 space-y-1.5 border border-gray-100 dark:border-white/[0.03]">
                                    <div className="flex justify-between items-center px-2 pb-1 text-[10px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest">
                                      <span>{t('transactions.details.description')}</span>
                                      <span>{t('transactions.details.amount')}</span>
                                    </div>
                                    <Divider className="!border-gray-100 dark:!border-white/5" />
                                    {tx.entries.map((entry) => {
                                      const entryIsPositive = matchesUserId(entry.receiverUserId, currentUserId) || (!matchesUserId(entry.senderUserId, currentUserId) && entry.amountMinor >= 0);
                                      return (
                                          <div key={entry.id} className="flex items-center justify-between px-2 py-1 text-xs">
                                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                              {entryIsPositive ? <TrendingUp className="text-green-500" sx={{ fontSize: 14 }} /> : <TrendingDown className="text-red-500" sx={{ fontSize: 14 }} />}
                                              <span>{formatEntryType(entry.entryType)}</span>
                                              {entry.onHold && (
                                                  <span className="text-[9px] bg-yellow-100 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-600 px-1 rounded font-bold uppercase">
                                      {t('transactions.status.on_hold')}
                                    </span>
                                              )}
                                            </div>
                                            <div className="text-right">
                                              {renderPrice(entry.amountMinor, entryIsPositive)}
                                            </div>
                                          </div>
                                      );
                                    })}
                                  </div>
                              ))}
                            </div>
                        )}
                      </div>
                  );
                })}
              </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 dark:border-white/5 mt-auto shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-400 font-bold uppercase">{t('transactions.pagination.rows')}</span>
            <Select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                size="small"
                className="bg-gray-50 dark:bg-neutral-800/50 text-gray-600 dark:text-gray-300 !text-[10px] rounded-md h-7"
                sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)' } }}
            >
              {[10, 20, 50].map(s => <MenuItem key={s} value={s} className="!text-xs">{s}</MenuItem>)}
            </Select>
          </div>
          <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': { color: '#9ca3af', borderColor: 'rgba(0,0,0,0.05)' },
                '& .Mui-selected': { backgroundColor: 'rgba(59, 130, 246, 0.1) !important', color: '#3b82f6 !important', fontWeight: 'bold' }
              }}
          />
          <div className="text-[10px] text-gray-400 font-mono">{totalElements} {t('transactions.pagination.total_items')}</div>
        </div>
      </div>
  );
};

export default UserTransactions;